import { PdfReportType } from 'api/Data/types';
import { axiosInstance } from 'api/instance';
import {
  PaginatedOrders,
  Model,
  Order,
  OrdersFilterParams,
  PaginatedTags,
  Tag,
  ModelEcr,
  ModelApi,
  NamedModel,
  ModelApiName,
  ModelsFilterParams, NamedModelsFilterParams, NamedModelHistory,
} from 'api/Exec/types';
import urls from 'api/Exec/urls';
import { PrsPdfMetadata } from 'app/common_components/PdfFormData/types';
import { FormValues } from 'components/Dashboard/Models/NamedModels/NamedModelForm/NamedModelForm';

import { AxiosResponse } from 'axios';

export const getOrdersRequest = (
  page = 1,
  pageSize = 10,
  filter: OrdersFilterParams,
  abortController?: AbortController,
): Promise<AxiosResponse<PaginatedOrders>> => {
  return axiosInstance.get<PaginatedOrders>(urls.ordersListUrl(page, pageSize, filter), {
    signal: abortController?.signal,
  });
};

export const createOrderRequest = (
  sourceFileId: string,
  modelName: string,
  tagsIds: string[],
  pdfMetadata: PrsPdfMetadata,
  pdfReportTypes: PdfReportType[],
): Promise<AxiosResponse<Order[]>> => {
  return axiosInstance.post<Order[]>(urls.ordersUrl, { sourceFileId, modelName, tagsIds, pdfMetadata, pdfReportTypes });
};

export const updateOrderRequest = (orderId: string, tagsIds: string[]): Promise<AxiosResponse<Order>> => {
  return axiosInstance.patch<Order>(urls.ordersItemUrl(orderId), { tagsIds });
};

export const getModelsRequest = (filter: ModelsFilterParams, { signal }: AbortController): Promise<AxiosResponse<Model[]>> => {
  return axiosInstance.get<Model[]>(urls.modelsListUrl(filter), { signal });
};

export const createModelRequest = (
  ecrImageId: string,
  modelApiName: ModelApiName,
  description?: string,
): Promise<AxiosResponse<Model>> => {
  return axiosInstance.post<Model>(urls.modelsUrl, { ecrImageId, modelApiName, description });
};

export const refreshModelsEcrRequest = (abortController?: AbortController): Promise<AxiosResponse<ModelEcr[]>> => {
  return axiosInstance.get<ModelEcr[]>(urls.modelsEcrUrl, { signal: abortController?.signal });
};

export const fetchModelsEcrRequest = (abortController?: AbortController): Promise<AxiosResponse<ModelEcr[]>> => {
  return axiosInstance.post<ModelEcr[]>(urls.modelsEcrUrl, { signal: abortController?.signal });
};

export const getModelsApiRequest = ({ signal }: AbortController): Promise<AxiosResponse<ModelApi[]>> => {
  return axiosInstance.get<ModelApi[]>(urls.modelsApiUrl, { signal });
};

export const getNamedModelsRequest = (
  filter?: NamedModelsFilterParams,
  abortController?: AbortController,
): Promise<AxiosResponse<NamedModel[]>> => {
  const filterParams: NamedModelsFilterParams = filter || {
    search: '',
    modelApiNames: [],
    imageTag: '',
    isSandbox: '',
    isCv: '',
    isEnabled: '',
  };
  return axiosInstance.get<NamedModel[]>(urls.namedModelsListUrl(filterParams), { signal: abortController?.signal });
};

export const checkNamedModelNameRequest = (namedModelName: string): Promise<AxiosResponse<NamedModel[]>> => {
  return axiosInstance.get<NamedModel[]>(urls.checkNamedModelNameUrl(namedModelName));
};

export const addNamedModelRequest = (values: FormValues): Promise<AxiosResponse<NamedModel>> => {
  return axiosInstance.post<NamedModel>(urls.namedModelsUrl, values);
};

export const updateNamedModelRequest = (namedModelId: string, values: FormValues): Promise<AxiosResponse<NamedModel>> => {
  return axiosInstance.put(urls.namedModelsItemUrl(namedModelId), values);
};

export const getNamedModelHistoryRequest = (
  namedModelId: string,
  abortController?: AbortController,
): Promise<AxiosResponse<NamedModelHistory[]>> => {
  return axiosInstance.get<NamedModelHistory[]>(urls.namedModelsItemHistoryUrl(namedModelId), { signal: abortController?.signal });
};

export const cancelOrderRequest = (orderId: string): Promise<AxiosResponse> => {
  return axiosInstance.post(urls.cancelOrderUrl, { orderId });
};

export const getTagsRequest = (
  page = 1,
  pageSize = 10,
  name = '',
  abortController?: AbortController,
): Promise<AxiosResponse<PaginatedTags>> => {
  return axiosInstance.get<PaginatedTags>(urls.tagsListUrl(page, pageSize, name), { signal: abortController?.signal });
};

export const createTagRequest = (name: string): Promise<AxiosResponse<Tag>> => {
  return axiosInstance.post<Tag>(urls.tagsUrl, { name });
};

export const deleteTagRequest = (tagId: string): Promise<AxiosResponse> => {
  return axiosInstance.delete(urls.tagsItemUrl(tagId));
};

export const updateTagRequest = (tagId: string, name: string): Promise<AxiosResponse<Tag>> => {
  return axiosInstance.put<Tag>(urls.tagsItemUrl(tagId), { name });
};
